import { useState, useCallback, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { PythonDocumentFilter, DocumentFilterSearchType } from '../../schemas/boolean_filtering';
import {
  ColumnFilter,
  getFilterableColumn,
  getDefaultSearchType,
  FILTERABLE_COLUMNS,
} from './filter-types';

/**
 * Hook for managing multiple filters in the DocumentGrid.
 * Supports multiple filters per column with different search types.
 */
export function useMultiFilters(initialFilters?: ColumnFilter[]) {
  const [filters, setFilters] = useState<ColumnFilter[]>(initialFilters ?? []);

  /**
   * Add a new filter, optionally for a specific column.
   */
  const addFilter = useCallback((columnId?: string) => {
    const targetColumnId = columnId ?? FILTERABLE_COLUMNS[0].id;
    const column = getFilterableColumn(targetColumnId);
    const defaultSearchType = column ? getDefaultSearchType(column) : 'CONTAINS';

    const newFilter: ColumnFilter = {
      id: uuidv4(),
      columnId: targetColumnId,
      value: '',
      searchType: defaultSearchType,
    };

    setFilters(prev => [...prev, newFilter]);
    return newFilter.id;
  }, []);

  /**
   * Update an existing filter.
   */
  const updateFilter = useCallback((id: string, updates: Partial<Omit<ColumnFilter, 'id'>>) => {
    setFilters(prev => prev.map(f => {
      if (f.id !== id) return f;

      const updated = { ...f, ...updates };

      // If column changed, reset search type to default for that column
      if (updates.columnId && updates.columnId !== f.columnId) {
        const column = getFilterableColumn(updates.columnId);
        if (column) {
          updated.searchType = getDefaultSearchType(column);
        }
      }

      return updated;
    }));
  }, []);

  /**
   * Remove a specific filter by ID.
   */
  const removeFilter = useCallback((id: string) => {
    setFilters(prev => prev.filter(f => f.id !== id));
  }, []);

  /**
   * Clear all filters.
   */
  const clearFilters = useCallback(() => {
    setFilters([]);
  }, []);

  /**
   * Clear all filters for a specific column.
   */
  const clearColumnFilters = useCallback((columnId: string) => {
    setFilters(prev => prev.filter(f => f.columnId !== columnId));
  }, []);

  /**
   * Get filters for a specific column.
   */
  const getFiltersForColumn = useCallback((columnId: string): ColumnFilter[] => {
    return filters.filter(f => f.columnId === columnId);
  }, [filters]);

  /**
   * Count filters for a specific column.
   */
  const countFiltersForColumn = useCallback((columnId: string): number => {
    return filters.filter(f => f.columnId === columnId && f.value.trim()).length;
  }, [filters]);

  /**
   * Check if there are any active filters (with non-empty values).
   */
  const hasActiveFilters = useMemo(() => {
    return filters.some(f => f.value.trim().length > 0);
  }, [filters]);

  /**
   * Get count of active filters (with non-empty values).
   */
  const activeFilterCount = useMemo(() => {
    return filters.filter(f => f.value.trim().length > 0).length;
  }, [filters]);

  /**
   * Convert filters to backend PythonDocumentFilter format.
   */
  const toDocumentFilters = useCallback((): PythonDocumentFilter[] => {
    return filters
      .filter(f => f.value.trim().length > 0)
      .map(f => ({
        field_name: f.columnId,
        field_value: f.value.trim(),
        search_type: f.searchType,
      }));
  }, [filters]);

  /**
   * Initialize filters from backend PythonDocumentFilter array.
   */
  const fromDocumentFilters = useCallback((docFilters: PythonDocumentFilter[]) => {
    const newFilters: ColumnFilter[] = docFilters.map(df => ({
      id: uuidv4(),
      columnId: df.field_name,
      value: df.field_value,
      searchType: (df.search_type ?? 'CONTAINS') as DocumentFilterSearchType,
    }));
    setFilters(newFilters);
  }, []);

  /**
   * Set filters directly (for restoring state).
   */
  const setFiltersDirectly = useCallback((newFilters: ColumnFilter[]) => {
    setFilters(newFilters);
  }, []);

  return {
    filters,
    addFilter,
    updateFilter,
    removeFilter,
    clearFilters,
    clearColumnFilters,
    getFiltersForColumn,
    countFiltersForColumn,
    hasActiveFilters,
    activeFilterCount,
    toDocumentFilters,
    fromDocumentFilters,
    setFiltersDirectly,
  };
}

export type UseMultiFiltersReturn = ReturnType<typeof useMultiFilters>;